import type { PresenterDepth } from "../domain";

export const DEPTH_STORAGE_KEY = "croc-ops.perspective.depth.v1";

export const presenterDepths = ["overview", "operations", "architecture"] as const;

export type DepthContent<T> = Record<PresenterDepth, T>;

export type DepthDefinition = {
  id: PresenterDepth;
  label: string;
  description: string;
  question: string;
  effect: string;
};

export const depthDefinitions: DepthContent<DepthDefinition> = {
  overview: {
    id: "overview",
    label: "Overview",
    description: "Plain-language impact and outcomes.",
    question: "What happened, who is affected, and what should happen next?",
    effect: "Condenses technical detail and leads with impact, ownership, and the next safe action.",
  },
  operations: {
    id: "operations",
    label: "Operations",
    description: "Evidence, ownership, and response detail.",
    question: "What evidence, owner, action, and consequence matter now?",
    effect: "Shows the complete working view: evidence, status, controls, consequences, and receipts.",
  },
  architecture: {
    id: "architecture",
    label: "Architecture",
    description: "Identifiers, dependencies, and data paths.",
    question: "Which records, relationships, and sources produced this state?",
    effect: "Expands stable IDs, relationship paths, source records, and provenance without changing access.",
  },
};

export const depthCoverageRoutes = [
  "operations",
  "incidents",
  "assets",
  "phone-detail",
  "infrastructure",
  "reports",
  "placeholder",
] as const;

export function isPresenterDepth(value: unknown): value is PresenterDepth {
  return typeof value === "string" && presenterDepths.includes(value as PresenterDepth);
}

export function defineDepthContent<T>(content: DepthContent<T>): DepthContent<T> {
  return content;
}

export function depthFromSearch(search: string): PresenterDepth | null | "invalid" {
  const params = new URLSearchParams(search);
  if (!params.has("depth")) return null;
  const value = params.get("depth");
  return isPresenterDepth(value) ? value : "invalid";
}

export function resolveInitialDepth(search: string, storedValue: unknown): PresenterDepth {
  const urlDepth = depthFromSearch(search);
  if (urlDepth === "invalid") return "operations";
  if (urlDepth) return urlDepth;
  return isPresenterDepth(storedValue) ? storedValue : "operations";
}

export function searchWithDepth(search: string, depth: PresenterDepth): string {
  const params = new URLSearchParams(search);
  params.set("depth", depth);
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function searchWithoutInvalidDepth(search: string): string {
  const params = new URLSearchParams(search);
  if (depthFromSearch(search) === "invalid") params.delete("depth");
  const next = params.toString();
  return next ? `?${next}` : "";
}
